import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router';
import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  Calendar,
  ChevronDown,
  Clock,
  FileSignature,
  CreditCard,
  FileText,
  FolderKanban,
  Inbox,
  LogOut,
  Plane,
  Settings,
  Stethoscope,
  Target,
  User as UserIcon,
  Users,
} from 'lucide-react';
import { useAuth } from './auth/AuthContext.tsx';
import { RequireAgent } from './auth/RequireAgent.tsx';
import { BroadcastBanner } from './components/BroadcastBanner.tsx';
import { GlobalSearch } from './components/GlobalSearch.tsx';
import { NotificationBell } from './components/NotificationBell.tsx';
import { NewVersionBanner } from './components/NewVersionBanner.tsx';
import { TimerIndicator } from './components/TimerIndicator.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { EnrollPage } from './pages/EnrollPage.tsx';
import { HealthPage } from './pages/HealthPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { OrganizationDetailPage } from './pages/OrganizationDetailPage.tsx';
import { OrganizationsListPage } from './pages/OrganizationsListPage.tsx';
import { ProjectDetailPage } from './pages/ProjectDetailPage.tsx';
import { CalendarPage } from './pages/CalendarPage.tsx';
import { ProjectsGanttPage } from './pages/ProjectsGanttPage.tsx';
import { ProjectsListPage } from './pages/ProjectsListPage.tsx';
import { ContractsListPage } from './pages/ContractsListPage.tsx';
import { TicketCreatePage } from './pages/TicketCreatePage.tsx';
import { TicketDetailPage } from './pages/TicketDetailPage.tsx';
import { TicketsListPage } from './pages/TicketsListPage.tsx';
import { AdminAgentsPage } from './pages/AdminAgentsPage.tsx';
import { AdminPortalInvitesPage } from './pages/AdminPortalInvitesPage.tsx';
import { AdminGoalsPage } from './pages/AdminGoalsPage.tsx';
import { AdminApiKeysPage } from './pages/AdminApiKeysPage.tsx';
import { AdminAuditLogPage } from './pages/AdminAuditLogPage.tsx';
import { AdminBroadcastsPage } from './pages/AdminBroadcastsPage.tsx';
import { AgentDetailPage } from './pages/AgentDetailPage.tsx';
import { AgentProfilePage } from './pages/AgentProfilePage.tsx';
import { AdminSkillsMatrixPage } from './pages/AdminSkillsMatrixPage.tsx';
import { AdminPtoPage } from './pages/AdminPtoPage.tsx';
import { PtoPage } from './pages/PtoPage.tsx';
import { TeamDirectoryPage } from './pages/TeamDirectoryPage.tsx';
import { AdminBulkImportPage } from './pages/AdminBulkImportPage.tsx';
import { AdminKbCategoriesPage } from './pages/AdminKbCategoriesPage.tsx';
import { AdminGooglePage } from './pages/AdminGooglePage.tsx';
import { AdminHuduPage } from './pages/AdminHuduPage.tsx';
import { AdminHarvestImportPage } from './pages/AdminHarvestImportPage.tsx';
import { AdminQwilrImportPage } from './pages/AdminQwilrImportPage.tsx';
import { AdminLandingPage } from './pages/AdminLandingPage.tsx';
import { AdminMicrosoftPage } from './pages/AdminMicrosoftPage.tsx';
import { CsatPage } from './pages/CsatPage.tsx';
import { canWriteQuotes } from '@t3/shared';
import { PublicQuotePage } from './pages/PublicQuotePage.tsx';
import { BillingSetupPage } from './pages/BillingSetupPage.tsx';
import { OverageReviewPage } from './pages/OverageReviewPage.tsx';
import { StripeEventsPage } from './pages/StripeEventsPage.tsx';
import { QuotesListPage } from './pages/QuotesListPage.tsx';
import { QuoteEditorPage } from './pages/QuoteEditorPage.tsx';
import { QuoteTemplatesPage } from './pages/QuoteTemplatesPage.tsx';
import { AdminSettingsPage } from './pages/AdminSettingsPage.tsx';
import { AdminSubtaskTemplatesPage } from './pages/AdminSubtaskTemplatesPage.tsx';
import { AdminZendeskImportPage } from './pages/AdminZendeskImportPage.tsx';
import { AdminZendeskTicketImportPage } from './pages/AdminZendeskTicketImportPage.tsx';
import { AdminTicketCsvOverridePage } from './pages/AdminTicketCsvOverridePage.tsx';
import { PortalBillingPage } from './pages/PortalBillingPage.tsx';
import { PortalApiKeysPage } from './pages/PortalApiKeysPage.tsx';
import { PortalWebhooksPage } from './pages/PortalWebhooksPage.tsx';
import { PortalDashboardPage } from './pages/PortalDashboardPage.tsx';
import { PortalProjectsListPage } from './pages/PortalProjectsListPage.tsx';
import { PortalProjectDetailPage } from './pages/PortalProjectDetailPage.tsx';
import { PortalEnrollPage } from './pages/PortalEnrollPage.tsx';
import { PortalKbCategoryPage } from './pages/PortalKbCategoryPage.tsx';
import { PortalTeamPage } from './pages/PortalTeamPage.tsx';
import { PortalProfilePage } from './pages/PortalProfilePage.tsx';
import { PortalReportsPage } from './pages/PortalReportsPage.tsx';
import { PortalTicketCreatePage } from './pages/PortalTicketCreatePage.tsx';
import { PortalTicketDetailPage } from './pages/PortalTicketDetailPage.tsx';
import { PortalTicketsListPage } from './pages/PortalTicketsListPage.tsx';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.tsx';
import { PasswordResetPage } from './pages/PasswordResetPage.tsx';
import { ReportsHealthPage } from './pages/ReportsHealthPage.tsx';
import { ReportsTechnicianPerformancePage } from './pages/ReportsTechnicianPerformancePage.tsx';
import { ReportsClientHoursPage } from './pages/ReportsClientHoursPage.tsx';
import { TimesheetsPage } from './pages/TimesheetsPage.tsx';
import { KbArticlePage } from './pages/KbArticlePage.tsx';
import { KbEditorPage } from './pages/KbEditorPage.tsx';
import { KbImportZendeskPage } from './pages/KbImportZendeskPage.tsx';
import { KbListPage } from './pages/KbListPage.tsx';
import { GoalsPage } from './pages/GoalsPage.tsx';
import { TimerPanel } from './timer/TimerPanel.tsx';

// Contracts used to live at /contracts. `Navigate` alone would drop the query
// string, and that is where the sidebar view and every filter live — so a
// shared "look at the unsigned ones" link would land on an unfiltered board.
function ContractsRedirect(): React.ReactElement {
  const { search } = useLocation();
  return <Navigate to={`/admin/contracts${search}`} replace />;
}

// The agent app's content width: 1200px of content inside a 1248px box
// (px-6 either side). Was max-w-6xl, which left 1104px — enough for most
// pages but tight for the wide tables (contracts, timesheets, reports),
// where the last column ended up behind a horizontal scrollbar. The header
// and the page body share this so the nav stays aligned with the content
// under it; change them together or they drift.
const SHELL_WIDTH = 'max-w-[1248px]';

export function App(): React.ReactElement {
  const { agent, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // The standalone timer window: no header, nav, or page container — it is
  // rendered into a ~340px popup (the fallback for browsers without Document
  // Picture-in-Picture) and is pinnable via the browser's "open as window".
  if (location.pathname === '/timer') {
    return agent ? (
      <TimerPanel compact />
    ) : (
      <div className="p-4 text-sm text-[var(--color-muted)]">
        <Link to="/login" className="text-[var(--color-t3-blue)] hover:underline">
          Sign in
        </Link>{' '}
        to use the timer.
      </div>
    );
  }

  // Portal pages render their own chrome; the agent header would be a
  // stacked second header for contacts.
  // Hide the agent header on portal routes AND on the public CSAT survey
  // landing page (clicked from an email — recipient shouldn't see admin
  // nav).
  const isPortalRoute =
    location.pathname.startsWith('/portal') ||
    location.pathname.startsWith('/csat/') ||
    location.pathname.startsWith('/quote/');
  // Portal pages paint their own nav bar + container internally — the outer
  // App container would double-wrap them. The unauth pages (login/enroll/reset)
  // and the password-reset/forgot pages should still be wrapped.
  const isPortalFullBleed =
    location.pathname === '/portal' ||
    location.pathname === '/portal/team' ||
    location.pathname === '/portal/profile' ||
    location.pathname === '/portal/api-keys' ||
    location.pathname === '/portal/webhooks' ||
    location.pathname === '/portal/projects' ||
    location.pathname.startsWith('/portal/projects/') ||
    location.pathname === '/portal/billing' ||
    location.pathname === '/portal/reports' ||
    location.pathname === '/portal/tickets' ||
    location.pathname === '/portal/tickets/new' ||
    location.pathname.startsWith('/portal/tickets/') ||
    location.pathname.startsWith('/portal/kb') ||
    location.pathname.startsWith('/csat/') ||
    location.pathname.startsWith('/quote/');

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `inline-flex items-center gap-1.5 ${isActive ? 'text-[var(--color-t3-blue)]' : 'text-white/80 hover:text-white'}`;
  const reportsActive =
    location.pathname === '/reports' || location.pathname.startsWith('/reports/');

  return (
    <div className="min-h-full">
      <NewVersionBanner />
      {agent && <GlobalSearch />}
      {!isPortalRoute && (
      <header className="bg-[var(--color-header)] text-white">
        <div className={`mx-auto flex ${SHELL_WIDTH} items-center gap-5 px-6 py-4`}>
          <Link
            to={agent ? '/dashboard' : '/login'}
            className="flex shrink-0 items-center gap-2 whitespace-nowrap text-lg font-semibold tracking-tight"
          >
            <img src="/Icon.png" alt="T3" className="h-7 w-7" />
            T3 Tick3ting
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            {agent && (
              <>
                <NavLink to="/tickets" className={navLinkClass}>
                  <Inbox className="h-4 w-4" /> Tickets
                </NavLink>
                <NavLink to="/projects" className={navLinkClass}>
                  <FolderKanban className="h-4 w-4" /> Projects
                </NavLink>
                <NavLink to="/calendar" className={navLinkClass}>
                  <Calendar className="h-4 w-4" /> Calendar
                </NavLink>
                <NavLink to="/kb" className={navLinkClass}>
                  <BookOpen className="h-4 w-4" /> KB
                </NavLink>
                <NavLink to="/team" className={navLinkClass}>
                  <Users className="h-4 w-4" /> Team
                </NavLink>
                <NavLink to="/reports/health" className={() => navLinkClass({ isActive: reportsActive })}>
                  <BarChart3 className="h-4 w-4" /> Reports
                </NavLink>
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-4 text-sm">
            <TimerIndicator />
            {agent && <NotificationBell />}
            {agent && (
              <NavDropdown
                label={
                  <>
                    <Settings className="h-4 w-4" /> Admin
                  </>
                }
              >
                <DropdownLink to="/organizations" icon={<Building2 className="h-4 w-4" />}>
                  Organizations
                </DropdownLink>
                {/* Contracts lives here rather than in the main nav row: it is
                    a billing surface, not a team one, and the top row is what
                    every technician looks at all day. A billing admin cannot
                    open /admin, so this is their only way in — hence its own
                    entry rather than relying on the Admin landing page. */}
                {canWriteQuotes(agent.role) && (
                  <DropdownLink to="/admin/quotes" icon={<FileText className="h-4 w-4" />}>
                    Quotes
                  </DropdownLink>
                )}
                {(agent.role === 'admin' || agent.role === 'billing_admin') && (
                  <DropdownLink to="/admin/contracts" icon={<FileSignature className="h-4 w-4" />}>
                    Contracts
                  </DropdownLink>
                )}
                {(agent.role === 'admin' || agent.role === 'billing_admin') && (
                  <DropdownLink to="/admin/billing/overage" icon={<CreditCard className="h-4 w-4" />}>
                    Overage to bill
                  </DropdownLink>
                )}
                {(agent.role === 'admin' || agent.role === 'billing_admin') && (
                  <DropdownLink to="/admin/billing/stripe" icon={<Activity className="h-4 w-4" />}>
                    Stripe events
                  </DropdownLink>
                )}
                {agent.role === 'admin' && (
                  <DropdownLink to="/admin" icon={<Settings className="h-4 w-4" />}>
                    Admin
                  </DropdownLink>
                )}
                <DropdownLink to="/health" icon={<Stethoscope className="h-4 w-4" />}>
                  Health
                </DropdownLink>
              </NavDropdown>
            )}
            {agent ? (
              <NavDropdown
                label={
                  <>
                    <UserIcon className="h-4 w-4" /> {agent.name.split(' ')[0]}
                  </>
                }
              >
                <DropdownLink to="/profile" icon={<UserIcon className="h-4 w-4" />}>
                  My profile
                </DropdownLink>
                <DropdownLink to="/timesheets" icon={<Clock className="h-4 w-4" />}>
                  My timesheets
                </DropdownLink>
                <DropdownLink to="/goals" icon={<Target className="h-4 w-4" />}>
                  My goals
                </DropdownLink>
                <DropdownLink to="/pto" icon={<Plane className="h-4 w-4" />}>
                  My PTO
                </DropdownLink>
                <DropdownButton
                  icon={<LogOut className="h-4 w-4" />}
                  onClick={async () => {
                    await logout();
                    navigate('/login', { replace: true });
                  }}
                >
                  Sign out
                </DropdownButton>
              </NavDropdown>
            ) : (
              <Link to="/login" className="hover:text-[var(--color-t3-blue)]">
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>
      )}
      {agent && !isPortalRoute && <BroadcastBanner audience="agent" />}

      <main className={isPortalFullBleed ? '' : `mx-auto ${SHELL_WIDTH} px-6 py-10`}>
        <Routes>
          <Route path="/" element={<Navigate to={agent ? '/dashboard' : '/login'} replace />} />
          <Route path="/csat/:token" element={<CsatPage />} />
          {/* Public, token-gated. The recipient is a client — often not
              even a portal contact — so no agent chrome, like /csat. */}
          <Route path="/quote/:token" element={<PublicQuotePage />} />
          <Route path="/billing-setup/:token" element={<BillingSetupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/enroll/:token" element={<EnrollPage />} />
          <Route path="/portal/enroll/:token" element={<PortalEnrollPage />} />
          <Route path="/forgot" element={<ForgotPasswordPage />} />
          <Route path="/reset/:token" element={<PasswordResetPage audience="agent" />} />
          <Route path="/portal/reset/:token" element={<PasswordResetPage audience="contact" />} />
          <Route path="/portal" element={<PortalDashboardPage />} />
          <Route path="/portal/tickets" element={<PortalTicketsListPage />} />
          <Route path="/portal/tickets/new" element={<PortalTicketCreatePage />} />
          <Route path="/portal/tickets/:id" element={<PortalTicketDetailPage />} />
          <Route path="/portal/profile" element={<PortalProfilePage />} />
          <Route path="/portal/api-keys" element={<PortalApiKeysPage />} />
          <Route path="/portal/webhooks" element={<PortalWebhooksPage />} />
          <Route path="/portal/projects" element={<PortalProjectsListPage />} />
          <Route path="/portal/projects/:id" element={<PortalProjectDetailPage />} />
          <Route path="/portal/billing" element={<PortalBillingPage />} />
          <Route path="/portal/hours" element={<Navigate to="/portal/reports" replace />} />
          <Route path="/portal/reports" element={<PortalReportsPage />} />
          <Route path="/portal/team" element={<PortalTeamPage />} />
          <Route
            path="/timesheets"
            element={
              <RequireAgent>
                <TimesheetsPage />
              </RequireAgent>
            }
          />
          <Route
            path="/goals"
            element={
              <RequireAgent>
                <GoalsPage />
              </RequireAgent>
            }
          />
          <Route
            path="/pto"
            element={
              <RequireAgent>
                <PtoPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/pto"
            element={
              <RequireAgent>
                <AdminPtoPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/goals"
            element={
              <RequireAgent>
                <AdminGoalsPage />
              </RequireAgent>
            }
          />
          <Route path="/reports" element={<Navigate to="/reports/health" replace />} />
          <Route
            path="/reports/health"
            element={
              <RequireAgent>
                <ReportsHealthPage />
              </RequireAgent>
            }
          />
          <Route
            path="/reports/technicians"
            element={
              <RequireAgent>
                <ReportsTechnicianPerformancePage />
              </RequireAgent>
            }
          />
          <Route
            path="/reports/client-hours"
            element={
              <RequireAgent>
                <ReportsClientHoursPage />
              </RequireAgent>
            }
          />
          <Route
            path="/kb"
            element={
              <RequireAgent>
                <KbListPage scope="agent" />
              </RequireAgent>
            }
          />
          <Route
            path="/kb/new"
            element={
              <RequireAgent>
                <KbEditorPage mode="create" />
              </RequireAgent>
            }
          />
          <Route
            path="/kb/import-zendesk"
            element={
              <RequireAgent>
                <KbImportZendeskPage />
              </RequireAgent>
            }
          />
          <Route
            path="/kb/:slug"
            element={
              <RequireAgent>
                <KbArticlePage scope="agent" />
              </RequireAgent>
            }
          />
          <Route
            path="/kb/:slug/edit"
            element={
              <RequireAgent>
                <KbEditorPage mode="edit" />
              </RequireAgent>
            }
          />
          <Route path="/portal/kb" element={<KbListPage scope="portal" />} />
          <Route path="/portal/kb/c/:slug" element={<PortalKbCategoryPage />} />
          <Route path="/portal/kb/:slug" element={<KbArticlePage scope="portal" />} />
          <Route path="/health" element={<HealthPage />} />
          <Route
            path="/dashboard"
            element={
              <RequireAgent>
                <DashboardPage />
              </RequireAgent>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAgent>
                <AgentProfilePage />
              </RequireAgent>
            }
          />
          <Route
            path="/agents/:id"
            element={
              <RequireAgent>
                <AgentDetailPage />
              </RequireAgent>
            }
          />
          <Route
            path="/team"
            element={
              <RequireAgent>
                <TeamDirectoryPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/skills-matrix"
            element={
              <RequireAgent>
                <AdminSkillsMatrixPage />
              </RequireAgent>
            }
          />
          <Route
            path="/organizations"
            element={
              <RequireAgent>
                <OrganizationsListPage />
              </RequireAgent>
            }
          />
          <Route
            path="/organizations/:id"
            element={
              <RequireAgent>
                <OrganizationDetailPage />
              </RequireAgent>
            }
          />
          <Route
            path="/projects"
            element={
              <RequireAgent>
                <ProjectsListPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/contracts"
            element={
              <RequireAgent>
                <ContractsListPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/billing/stripe"
            element={
              <RequireAgent>
                <StripeEventsPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/billing/overage"
            element={
              <RequireAgent>
                <OverageReviewPage />
              </RequireAgent>
            }
          />
          {/* Contracts moved under /admin. Bookmarks and links shared while it
              sat at the top level still land on the board — with their filters,
              since the sidebar views live in the query string. */}
          <Route path="/contracts" element={<ContractsRedirect />} />
          {/* Quotes sit beside Contracts, and for the same reason: what we are
              selling and for how much is not a surface the whole team works
              from. The server gates these routes too — this only decides
              where the door is. */}
          <Route
            path="/admin/quotes"
            element={
              <RequireAgent>
                <QuotesListPage />
              </RequireAgent>
            }
          />
          {/* Before /:ticketId, or 'templates' would be read as a ticket id. */}
          <Route
            path="/admin/quotes/templates"
            element={
              <RequireAgent>
                <QuoteTemplatesPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/quotes/:ticketId"
            element={
              <RequireAgent>
                <QuoteEditorPage />
              </RequireAgent>
            }
          />
          <Route
            path="/projects/gantt"
            element={
              <RequireAgent>
                <ProjectsGanttPage />
              </RequireAgent>
            }
          />
          <Route path="/projects/calendar" element={<Navigate to="/calendar" replace />} />
          <Route
            path="/calendar"
            element={
              <RequireAgent>
                <CalendarPage />
              </RequireAgent>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <RequireAgent>
                <ProjectDetailPage />
              </RequireAgent>
            }
          />
          <Route
            path="/tickets"
            element={
              <RequireAgent>
                <TicketsListPage />
              </RequireAgent>
            }
          />
          <Route
            path="/tickets/new"
            element={
              <RequireAgent>
                <TicketCreatePage />
              </RequireAgent>
            }
          />
          <Route
            path="/tickets/:id"
            element={
              <RequireAgent>
                <TicketDetailPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAgent>
                <AdminLandingPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/agents"
            element={
              <RequireAgent>
                <AdminAgentsPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/portal-invites"
            element={
              <RequireAgent>
                <AdminPortalInvitesPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/google"
            element={
              <RequireAgent>
                <AdminGooglePage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/microsoft"
            element={
              <RequireAgent>
                <AdminMicrosoftPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequireAgent>
                <AdminSettingsPage />
              </RequireAgent>
            }
          />
          <Route path="/admin/reports" element={<Navigate to="/reports/health" replace />} />
          <Route
            path="/admin/import-zendesk-orgs"
            element={
              <RequireAgent>
                <AdminZendeskImportPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/import-zendesk-tickets"
            element={
              <RequireAgent>
                <AdminZendeskTicketImportPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/ticket-csv-override"
            element={
              <RequireAgent>
                <AdminTicketCsvOverridePage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/bulk-import"
            element={
              <RequireAgent>
                <AdminBulkImportPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/subtask-templates"
            element={
              <RequireAgent>
                <AdminSubtaskTemplatesPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/kb-categories"
            element={
              <RequireAgent>
                <AdminKbCategoriesPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              <RequireAgent>
                <AdminApiKeysPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/hudu"
            element={
              <RequireAgent>
                <AdminHuduPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/harvest-import"
            element={
              <RequireAgent>
                <AdminHarvestImportPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/qwilr-import"
            element={
              <RequireAgent>
                <AdminQwilrImportPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <RequireAgent>
                <AdminAuditLogPage />
              </RequireAgent>
            }
          />
          <Route
            path="/admin/broadcasts"
            element={
              <RequireAgent>
                <AdminBroadcastsPage />
              </RequireAgent>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function NavDropdown({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-white/80 hover:text-white"
      >
        {label}
        <ChevronDown className={'h-3.5 w-3.5 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white py-1 text-[var(--color-body)] shadow-xl"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 px-3.5 py-2 text-sm hover:bg-[var(--color-surface)]"
    >
      <span className="text-[var(--color-muted)]">{icon}</span>
      {children}
    </Link>
  );
}

function DropdownButton({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void | Promise<void>;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm hover:bg-[var(--color-surface)]"
    >
      <span className="text-[var(--color-muted)]">{icon}</span>
      {children}
    </button>
  );
}
